import React, { useCallback, useEffect, useRef, useState } from "react";
import { Modal } from "Components/Modal";
import { Body1, Body2, Subtitle2, Error } from "melodies-source/Text";
import styled, { css } from "styled-components";
import { Button, Divider, useArtistContext } from "Components";
import { ReactComponent as MagicIcon } from "assets/svg/magic.svg";
import { RequestSubmittedModal } from "./RequestSubmittedModal";
import { Field, FieldProps, Form, FormikProvider, useFormik } from "formik";
import * as yup from "yup";
import { useQuery } from "react-query";
import { api } from "api";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getFirestore,
  setDoc,
  writeBatch,
} from "firebase/firestore";
import { useUser } from "auth";
import { SvgCheckCircle } from "melodies-source/Svgs/CheckCircle";
import { SvgErrorAlt } from "melodies-source/Svgs/ErrorAlt";
import { useArtistGroupSubdomain } from "hooks/useArtistGroupSubdomains";
import toast, { LoaderIcon } from "react-hot-toast";
import { Spinner } from "melodies-source/Spinner";
import { AiHandleOut } from "@max/common/src/api/ai";
import { ModalHeaderTitle } from "melodies-source/Modal";
import { ReactComponent as LinkIcon } from "assets/svg/link-alt.svg";
import { debounce } from "Utils/debounce";
import { Ref } from "refs";

interface Values {
  subdomain: string;
}

const INITIAL_VALUES: Values = {
  subdomain: "",
};

const validateSubdomain = debounce(
  async (value: string, resolve: (valid: boolean) => void) => {
    if (value) {
      const subdomain = await getDoc(
        doc(getFirestore(), "artist_subdomain", value),
      );
      resolve(!subdomain.exists());
    } else {
      resolve(true);
    }
  },
  500,
);

const VALIDATION_SCHEMA: yup.SchemaOf<Values> = yup.object().shape({
  subdomain: yup
    .string()
    .required("Please enter a subdomain.")
    .matches(
      /^[a-z0-9]+(?:-[a-z0-9]+)*$/,
      "Please choose a subdomain without special characters.",
    )
    .test("verify subdomain", "This subdomain is not available.", (value) => {
      return new Promise((resolve) => {
        validateSubdomain(value, resolve);
      });
    }),
});

interface Props {
  isOpen: boolean;
  onClose: () => void;
  mode: "create" | "change";
}

export const ArtistSubdomainModal: React.FC<Props> = ({
  isOpen,
  onClose,
  mode,
}) => {
  const [submitted, setSubmitted] = useState(false);
  const formButtonRef = useRef<HTMLButtonElement | null>(null);
  const artist = useArtistContext();
  const [subdomain] = useArtistGroupSubdomain();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { user, claims } = useUser();
  const inputRef = useRef<HTMLDivElement | null>(null);
  const inputTextRef = useRef<HTMLSpanElement | null>(null);
  const inputSuffixRef = useRef<HTMLSpanElement | null>(null);
  const inputElementRef = useRef<HTMLInputElement | null>(null);
  const initialInputWidth = useRef(0);
  const [inputScrolledToEnd, setInputScrolledToEnd] = useState(true);
  const debouncedSetInputStyle = useCallback(
    debounce(() => setInputStyle(), 100),
    [],
  );
  const isSuperAdmin = claims.admin;
  const formik = useFormik({
    initialValues: INITIAL_VALUES,
    validationSchema: VALIDATION_SCHEMA,
    validateOnBlur: false,
    onSubmit: isSuperAdmin
      ? handleUpdate
      : mode === "create"
      ? handleCreate
      : handleChange,
  });
  const aiSubdomain = useQuery({
    queryKey: ["aiSubdomain", artist?.name],
    enabled: !isSuperAdmin,
    queryFn: async () => {
      const response = await api.call("ai.handle", {
        artistName: artist.name,
      });
      return response.data.data;
    },
    onSuccess: (aiSubdomain: AiHandleOut) => {
      formik.setFieldValue("subdomain", aiSubdomain.subdomain);
      setInputStyle(aiSubdomain.subdomain);
      verifyInputScrolledToEnd();
    },
    onError: (error) => {
      console.error(error);
    },
  });

  async function handleCreate(values: Values) {
    try {
      await setDoc(doc(getFirestore(), "artist_subdomain", values.subdomain), {
        artistGroupId: artist.id,
      });
      onClose();
      toast.success("Subdomain created successfully");
    } catch (error) {
      console.error(error);
      toast.error("There was an error creating your subdomain");
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleChange(values: Values) {
    try {
      await addDoc(collection(getFirestore(), "contact_us"), {
        code: "artist-portal",
        name: user.displayName,
        email: user.email,
        reason: "Domain Change",
        message: values.subdomain,
      });
      onClose();
      setSubmitted(true);
    } catch (error) {
      console.error(error);
      toast.error("There was an error submitting your request");
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleUpdate(values: Values) {
    const db = getFirestore();
    const batch = writeBatch(db);
    try {
      const previousSubdomain = subdomain.id;
      const newSubdomainRef = Ref("artist_subdomain/{domain}", {
        domain: values.subdomain,
      });
      const oldSubdomainRef = Ref("artist_subdomain/{domain}", {
        domain: previousSubdomain,
      });

      batch.set(newSubdomainRef, subdomain.data());
      batch.delete(oldSubdomainRef);

      await batch.commit();

      onClose();
      toast.success("Subdomain updated successfully");
    } catch (error) {
      console.error(error);
      toast.error("There was an error updating subdomain");
    } finally {
      setIsSubmitting(false);
    }
  }

  const getInputWidth = () => inputTextRef.current?.clientWidth || 0;
  const getInputMaxWidth = () =>
    initialInputWidth.current - 5 - (inputSuffixRef.current?.clientWidth || 0);

  const setInputStyle = (value?: string) => {
    if (!inputTextRef.current) return;
    if (value !== undefined) inputTextRef.current.textContent = value;

    inputElementRef.current?.setAttribute(
      "style",
      `
      width: ${getInputWidth()}px;
      max-width: ${getInputMaxWidth()}px
    `,
    );
  };

  function verifyInputScrolledToEnd() {
    const inputElem = inputElementRef.current;
    if (inputElem) {
      const inputMaxWidth = getInputMaxWidth();
      const inputWidth = getInputWidth();

      setInputScrolledToEnd(
        inputWidth <= inputMaxWidth ||
          inputMaxWidth + inputElem.scrollLeft + 1 >= inputWidth,
      );
    }
  }

  // Set the subdomain to input if subdomain exists
  useEffect(() => {
    if (inputRef.current && subdomain) {
      formik.setFieldValue("subdomain", subdomain.id);
      setInputStyle(subdomain.id);
    }
  }, [inputRef.current, subdomain?.id]);

  // Apply style to input after populating ai subdomain
  useEffect(() => {
    if (inputRef.current && formik.values.subdomain) {
      setInputStyle(formik.values.subdomain);
    }
  }, [inputRef.current, formik.values.subdomain]);

  // Set initial input width when input is mounted
  useEffect(() => {
    if (inputRef.current) {
      initialInputWidth.current = inputRef.current?.clientWidth;
    }
  }, [inputRef.current]);

  // Verify input scrolled to end when input is mounted
  useEffect(() => {
    const inputElem = inputElementRef.current;
    verifyInputScrolledToEnd();
    if (inputElem) {
      inputElem.addEventListener("scroll", verifyInputScrolledToEnd);
    }
    return () =>
      inputElem?.removeEventListener("scroll", verifyInputScrolledToEnd);
  }, [inputElementRef.current]);

  // Add listener for window resize to update input style
  useEffect(() => {
    window.addEventListener("resize", debouncedSetInputStyle);
    return () => {
      window.removeEventListener("resize", debouncedSetInputStyle);
    };
  }, []);

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        header={
          <ModalHeader>
            <ArtistImageContainer>
              <AritstImage src={artist?.assets.icon?.path} />
              <LinkIconContainer>
                <LinkIcon />
              </LinkIconContainer>
            </ArtistImageContainer>
            <ModalHeaderTitle>
              {isSuperAdmin
                ? "Update Artist Subdomain"
                : mode === "create"
                ? "Create Your Artist Subdomain"
                : "Request Domain Change"}
            </ModalHeaderTitle>
          </ModalHeader>
        }
        headerColor="navy"
        actions={{
          main: {
            text: isSuperAdmin ? "Update" : subdomain ? "Submit" : "Create",
            onClick: () => {
              setIsSubmitting(true);
              formButtonRef.current?.click();
            },
            loading: isSubmitting,
            disabled: subdomain?.id === formik.values.subdomain,
          },
          secondary: {
            text: "Cancel",
            onClick: onClose,
          },
        }}
      >
        <Container>
          <Body1>
            {isSuperAdmin
              ? "Enter new subdomain:"
              : mode === "create"
              ? "Your artist subdomain links fans to featured SET.Fan campaigns and is used for your SET.Bio page."
              : "If you want to change your subdomain, you can submit a request to the SET team."}
          </Body1>
          <FormikProvider value={formik}>
            <Form>
              <Field name="subdomain">
                {({ field, meta }: FieldProps) => (
                  <InputWrapper>
                    <InputContainer>
                      <Input
                        ref={inputRef}
                        onClick={() => inputElementRef.current?.focus()}
                      >
                        <InputElement
                          ref={inputElementRef}
                          type="text"
                          autoFocus={true}
                          autoCorrect="off"
                          autoCapitalize="none"
                          autoComplete="none"
                          onChange={(e) => {
                            const value = e.target.value.trim();
                            setInputStyle(value);
                            formik.setFieldError("subdomain", undefined);
                            formik.setFieldValue("subdomain", value);
                            verifyInputScrolledToEnd();
                          }}
                          onBlur={formik.handleBlur}
                          value={field.value}
                          disabled={aiSubdomain.isLoading}
                          style={{
                            width: 0,
                            minWidth: 0,
                          }}
                        />
                        {(aiSubdomain.isLoading || !inputTextRef.current) && (
                          <SubdomainInputLoading />
                        )}
                        <InputText ref={inputTextRef}></InputText>
                        <InputSuffix
                          ref={inputSuffixRef}
                          inputScrolledToEnd={inputScrolledToEnd}
                        >
                          {inputTextRef.current ? ".set.fan" : ""}
                        </InputSuffix>
                      </Input>
                      {subdomain?.id !== field.value && (
                        <InputIcon>
                          {!formik.isSubmitting && formik.isValidating ? (
                            <StyledLoaderIcon />
                          ) : (meta.touched || field.value) &&
                            formik.isValid ? (
                            <ValidIcon />
                          ) : meta.error ? (
                            <ErrorIcon />
                          ) : null}
                        </InputIcon>
                      )}
                    </InputContainer>
                    {subdomain?.id !== field.value &&
                      formik.errors.subdomain &&
                      !aiSubdomain.isLoading && (
                        <StyledError>{formik.errors.subdomain}</StyledError>
                      )}
                  </InputWrapper>
                )}
              </Field>
              <FormButton ref={formButtonRef} type="submit"></FormButton>
            </Form>
          </FormikProvider>
          {!isSuperAdmin && (
            <>
              {aiSubdomain.data && (
                <Subtitle2>
                  Your sub-domain is the "front door" link fans will use again
                  and again to find you on SET. Pick something timeless like{" "}
                  <b>{aiSubdomain?.data?.subdomain}.set.fan</b> or{" "}
                  <b>{aiSubdomain?.data?.alternate1}.set.fan</b>.
                </Subtitle2>
              )}
              <Divider />
              <div>
                <Body2>Need help picking a domain?</Body2>
                <Button
                  leftIcon={<MagicIcon />}
                  variant="outlined"
                  onClick={() => aiSubdomain.refetch()}
                  loading={aiSubdomain.isFetching}
                >
                  Domain Generator
                </Button>
              </div>
            </>
          )}
        </Container>
      </Modal>
      <RequestSubmittedModal
        isOpen={submitted}
        onClose={() => setSubmitted(false)}
      />
    </>
  );
};
const Container = styled.div`
  ${Body1} {
    margin-bottom: 20px;
  }

  ${Divider} {
    margin: 20px 0;
  }

  ${Subtitle2} {
    font-weight: 400;
    margin-top: 5px;
  }

  ${Body2} {
    text-align: center;
    margin-bottom: 10px;
  }

  button {
    width: -webkit-fill-available;
  }
`;

const ArtistImageContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  flex-shrink: 0;
  width: 70px;
  height: 70px;

  ${({ theme }) => theme.media.mobile} {
    width: 60px;
    height: 60px;
  }
`;

export const LinkIconContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  bottom: 0;
  right: 2px;
  width: 20px;
  height: 20px;
  background-color: var(--main-color);
  border-radius: 50%;
  border: 1px solid white;

  svg {
    width: 12px;
    height: 12px;
    color: white;
    transform: rotate(90deg);
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;

  ${ModalHeaderTitle} {
    color: var(--main-color);
  }
`;

const AritstImage = styled.img`
  border-radius: 50%;
`;

const FormButton = styled.button`
  display: none;
`;

const InputWrapper = styled.div`
  max-width: 466px;
  width: 100%;
  margin: 20px auto;

  ${(p) => p.theme.media.mobile} {
    margin: 11px auto 20px;
  }
`;

const InputContainer = styled.div`
  display: flex;
  height: 46px;
  padding: 0 20px;
  align-items: center;
  border-radius: 5px;
  flex-wrap: wrap;
  border: 1px solid #cccccc;
  border-radius: 6px;

  ${(p) => p.theme.media.mobile} {
    height: 45px;
  }
`;

interface InputSuffixProps {
  inputScrolledToEnd: boolean;
}

const InputSuffix = styled.span<InputSuffixProps>`
  font-size: 16px;
  line-height: 18px;
  color: #999999;
  flex-shrink: 0;
  user-select: none;
  position: relative;

  ${(p) => p.theme.media.mobile} {
    font-size: 15px;
    line-height: 22px;
  }

  ${({ inputScrolledToEnd }) =>
    !inputScrolledToEnd &&
    css`
      &::before {
        content: "";
        width: 20px;
        height: 30px;
        background: linear-gradient(90deg, #dde5eb12 0%, #ffffff 100%);
        position: absolute;
        top: 0;
        left: -20px;
      }
    `}
`;

const Input = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  flex: 1;
  position: relative;
  overflow: hidden;
`;

const InputElement = styled.input`
  border: 0;
  margin: 0;
  padding: 0;
  font-size: 16px;

  color: #333333;
  outline: 0;

  &:disabled {
    background: none;
  }

  ${(p) => p.theme.media.mobile} {
    font-size: 15px;
    line-height: 22px;
  }
`;

const InputText = styled.span`
  position: absolute;
  top: 0;
  font-size: 16px;
  visibility: hidden;

  ${(p) => p.theme.media.mobile} {
    font-size: 15px;
  }
`;

const InputIcon = styled.div`
  display: flex;
  justify-content: center;
  flex-shrink: 0;
  margin-left: 10px;
  width: 30px;

  ${(p) => p.theme.media.mobile} {
    width: 20px;
    margin-left: 0px;
  }
`;

const iconStyle = css`
  width: 25px;
  height: 25px;

  ${(p) => p.theme.media.mobile} {
    width: 20px;
    height: 20px;
  }
`;

const ValidIcon = styled(SvgCheckCircle)`
  ${iconStyle}
  color: #1dab98;
`;

const ErrorIcon = styled(SvgErrorAlt)`
  ${iconStyle}
  color: #ff0000;
`;

const StyledLoaderIcon = styled(LoaderIcon)`
  width: 15px;
  height: 15px;
`;

const SubdomainInputLoading = styled(Spinner)`
  position: absolute;
  left: 0;
  width: 70px;
`;

const StyledError = styled(Error)`
  margin-top: 5px;

  span {
    color: #00e;
    text-decoration: underline;
    cursor: pointer;
  }

  ${({ theme }) => theme.media.mobile} {
    font-size: 11px;
  }
`;
